import React, {createContext, useContext, useEffect, useRef} from 'react'
import _ from 'lodash'
import * as Y from 'yjs'
import {IndexeddbPersistence} from 'y-indexeddb'
import {useGet, useMutate} from 'restful-react'
import {AuthContext} from '@/features/auth'
import {LegacyCatalogContext} from '@/features/data-sources'
import {MessageContext} from '@/features/errors'

export interface IEventIdentity {
  type: string
  start: number
  end?: number
  [key: string]: any
}

export interface IEvent {
  type?: string
  group?: string
  start: number
  end?: number
  hue?: number
  text?: string
  hidden?: boolean
  frame_image?: string
  rect?: {
    x: number | number[]
    y: number | number[]
    w: number | number[]
    h: number | number[]
  }
  [key: string]: any
}

export interface ILabel<T extends object> extends IEventIdentity {
  data?: T
}

export interface IUserLabelsTrack<T extends ILabel<object> = ILabel<object>> {
  [start: string]: T
}

export interface IUserLabelsMap<T extends ILabel<object> = ILabel<object>> {
  [type: string]: IUserLabelsTrack<T>
}

export interface ILabelingContext {
  isLabelingAllowed: boolean
  userLabelResponses: {[id: string]: IGetUserLabelsResponse}
  fetchApiLabels: (
    catalog: string,
    vionlabs_id: string
  ) => Promise<IGetUserLabelsResponse | undefined>
  getCrdtLabelTrackCollection: (vionlabs_id: string) => Y.Map<Y.Map<ILabel<object> | undefined>>
  getCrdtLabelTrack: (
    vionlabs_id: string,
    trackName: string
  ) => Y.Map<ILabel<object> | undefined> | undefined
  haveLocalChanges: (vionlabs_id: string) => boolean
  syncUpstreamLabels: (
    catalog: string,
    vionlabs_id: string,
    syncDown?: boolean,
    syncUp?: boolean
  ) => Promise<void>
  // exportLabels: () => void,
  // exportVerifiedPredictions: () => void,
}

export interface ILabelingProps {
  disabled?: boolean
  children: React.ReactNode
}

export interface IGetUserLabelsPathParams {
  catalog: string
}

export interface IGetUserLabelsQueryParams {
  user_id: string
  vionlabs_id: string
}

export interface IGetUserLabelsResponseItemLabel {
  user_id: string
  prediction: IEvent
  label_data: object | null
}

export interface IGetUserLabelsResponseItem {
  vionlabs_id: string
  labels: IGetUserLabelsResponseItemLabel[]
}

export interface IGetUserLabelsResponse {
  vionlabs_id: string
  user_id: string
  items: IGetUserLabelsResponseItem[]
  offset: number
  limit?: number
  total: number
}

export interface IMutateUserLabelsPatchItem {
  vionlabs_id: string
  prediction: IEvent
  label_data: object | null | undefined
}

export interface ILabelTracks {
  isBusy: boolean
  canLabel: boolean
  apiLabels?: IGetUserLabelsResponse
  crdtLabels?: Y.Map<Y.Map<ILabel<object> | undefined>>
}

// TODO: product codes?
// export type APIProductTypes = string;
// export type APILabelingFileTypes = 'prediction' | 'label';

export const LabelingContext = createContext<ILabelingContext>({
  isLabelingAllowed: false,
  userLabelResponses: {},
  fetchApiLabels: () => {
    throw new Error('Not implemented')
  },
  getCrdtLabelTrackCollection: () => {
    throw new Error('Not implemented')
  },
  getCrdtLabelTrack: () => {
    throw new Error('Not implemented')
  },
  // exportLabels: () => {throw new Error("Not implemented")},
  // exportVerifiedPredictions: () => {throw new Error("Not implemented")},
  haveLocalChanges: () => {
    throw new Error('Not implemented')
  },
  syncUpstreamLabels: () => {
    throw new Error('Not implemented')
  },
})

export function eventIdentityFromEvent(event: IEvent): IEventIdentity | undefined {
  if (!event.type) return
  const res: IEventIdentity = {
    type: _.toLower(event.type),
    start: event.start,
  }
  if (event.end !== undefined) res.end = event.end
  return res
}

export function parseEvent(event: any): IEvent {
  return {
    ...event,
    ...eventIdentityFromEvent(event),
  }
}

export function LabelingContextWrapper(props: ILabelingProps) {
  const crdtDbName = `vionlabels-${window.location.host}`
  const apiLabelVersion = '_'

  const {disabled, children} = props

  const yjsRef = useRef(
    new Y.Doc({
      guid: crdtDbName,
    })
  )

  const {user, getCookies, havePermission} = useContext(AuthContext)

  const {logError} = useContext(MessageContext)

  const {apiCatalogName} = useContext(LegacyCatalogContext)

  const userLabelResponses = useRef<{[id: string]: IGetUserLabelsResponse}>({})

  const {refetch: refetchLabels} = useGet<
    IGetUserLabelsResponse,
    any,
    IGetUserLabelsQueryParams,
    IGetUserLabelsPathParams
  >({
    lazy: true,
    path: ({catalog}: {catalog?: string}) =>
      `/api/labels/catalogs/${catalog || apiCatalogName}/product_versions/${apiLabelVersion}/labels`,
  })

  const {mutate: mutateLabels} = useMutate<
    any,
    any,
    IGetUserLabelsQueryParams,
    any,
    IGetUserLabelsPathParams
  >({
    verb: 'PUT',
    path: ({catalog}: {catalog?: string}) =>
      `/api/labels/catalogs/${catalog || apiCatalogName}/product_versions/${apiLabelVersion}/labels`,
    requestOptions: () => ({
      headers: {
        'X-CSRFToken': getCookies().csrftoken,
      },
    }),

    // TODO: !!!!!
    // mock: {
    //     mutate: async (data) => {
    //         console.log(`MOCK MUTATE`, data)
    //         return;
    //     },
    // },
  })

  useEffect(() => {
    // console.log('init colab');
    new IndexeddbPersistence(crdtDbName, yjsRef.current)
    // new WebrtcProvider(
    //     dbName,
    //     yjsRef.current,
    //     {
    //         // TODO: self host?
    //         signaling: [
    //             'wss://signaling.yjs.dev',
    //             'wss://y-webrtc-signaling-eu.herokuapp.com',
    //             'wss://y-webrtc-signaling-us.herokuapp.com',
    //         ],
    //         // TODO: syncronized passwords? perhaps use websockets + JWT instead
    //         password: '',
    //     },
    // );
    return () => {
      // console.log('dispose colab');
      yjsRef.current.destroy()
    }
  }, [])

  function isLabelingAllowed() {
    if (disabled) return false
    return havePermission(['label.list_labels', 'label.add_label'])
  }

  async function fetchApiLabels(
    catalog: string,
    vionlabs_id: string,
    ignoreCache: boolean = false
  ): Promise<IGetUserLabelsResponse | undefined> {
    if (!vionlabs_id) return
    if (disabled) return
    if (!ignoreCache) {
      const cacheRes = _.get(userLabelResponses.current, vionlabs_id)
      if (cacheRes) return cacheRes
    }
    const res =
      havePermission('label.list_labels') ?
        await refetchLabels({
          pathParams: {
            catalog,
          },
          queryParams: {
            user_id: user.username,
            vionlabs_id: vionlabs_id,
          },
        })
      : undefined
    if (!res) return
    userLabelResponses.current = {
      ...userLabelResponses.current,
      [res.vionlabs_id]: res,
    }
    return res
  }

  function getCrdtLabelTrackCollection(
    vionlabs_id: string
  ): Y.Map<Y.Map<ILabel<object> | undefined>> {
    if (!vionlabs_id) throw new Error(`Invalid collection: ${vionlabs_id}`)
    const ydoc = yjsRef.current
    const root = ydoc.getMap<Y.Map<Y.Map<ILabel<object> | undefined>>>('video-labels')
    let collection: Y.Map<Y.Map<ILabel<object> | undefined>> | undefined
    if (root.has(vionlabs_id)) {
      collection = root.get(vionlabs_id)
    }
    if (collection) return collection
    collection = new Y.Map<Y.Map<ILabel<object> | undefined>>()
    root.set(vionlabs_id, collection)
    return collection
  }

  function getCrdtLabelTrack(
    vionlabs_id: string,
    trackName: string
  ): Y.Map<ILabel<object> | undefined> {
    if (!vionlabs_id || !trackName) throw new Error(`Invalid track: ${vionlabs_id}.${trackName}`)
    const labelCollection = getCrdtLabelTrackCollection(vionlabs_id)
    let labelTrack: Y.Map<ILabel<object> | undefined> | undefined
    if (labelCollection.has(trackName)) {
      labelTrack = labelCollection.get(trackName)
    }
    if (labelTrack) return labelTrack
    const track = new Y.Map<ILabel<object> | undefined>()
    labelCollection.set(trackName, track)
    return track
  }

  function haveLocalChanges(vionlabs_id: string): boolean {
    const crdt = getCrdtLabelTrackCollection(vionlabs_id)
    const crdtLabelMap: IUserLabelsMap = crdt.toJSON()
    const apiRes = _.get(userLabelResponses.current, vionlabs_id)
    const apiResMap = apiRes ? parseApiLabels(apiRes) : undefined
    const diff = labelMapDiffPairs(crdtLabelMap, apiResMap)
    return diff.length > 0
  }

  async function syncUpstreamLabels(
    catalog: string,
    vionlabs_id: string,
    syncDown = true,
    syncUp = true
  ): Promise<void> {
    if (disabled) return
    if (!isLabelingAllowed()) return
    const crdt = getCrdtLabelTrackCollection(vionlabs_id)
    const crdtLabelMap: IUserLabelsMap = crdt.toJSON()
    if (syncUp) {
      const apiRes = await fetchApiLabels(catalog, vionlabs_id) // maybe cached
      const apiResMap = apiRes ? parseApiLabels(apiRes) : undefined
      if (_.keys(apiResMap).length > 0 && _.keys(crdtLabelMap).length === 0) {
        logError('CRDT is empty, refusing sync')
        return
      }
      const patches = labelMapDiffAsApiPatches(vionlabs_id, apiResMap, crdtLabelMap)
      if (patches.length > 0) {
        await mutateLabels(patches, {
          pathParams: {
            catalog,
          },
        })
      }
    }
    if (syncDown) {
      const apiRes = await fetchApiLabels(catalog, vionlabs_id, true) // ignores cache if it might have changed
      const apiResMap = apiRes ? parseApiLabels(apiRes) : undefined
      const diff = labelMapDiffPairs(crdtLabelMap, apiResMap)
      _.chain(diff)
        .forEach(([key, _a, b]) => {
          // TODO: without regex?
          const _keyComponents = key.match(/\["(.*?)"\]\["(.*?)"\]/)
          if (!_keyComponents) return
          const track = getCrdtLabelTrack(vionlabs_id, _keyComponents[1])
          if (!track) return
          if (b) track.set(_keyComponents[2], b)
          else track.delete(_keyComponents[2])
        })
        .value()
    }
  }

  function parseApiLabels(apiResponse: IGetUserLabelsResponse): IUserLabelsMap {
    const res: IUserLabelsMap = _.chain(apiResponse)
      .get('items')
      .first()
      .get('labels')
      .groupBy((label) => label.prediction.type)
      .toPairs()
      .map(([k, v]) => [
        k,
        _.chain(v)
          .groupBy((vv) => vv.prediction.start)
          .toPairs()
          .map(([kk, vv]) => [
            kk,
            // TODO: solve for label type/ts collisions?
            _.chain(vv)
              .map(({prediction, label_data}) => {
                if (!prediction.type) {
                  throw new Error('Missing type in response label')
                }
                const resItem = _.chain<ILabel<object>>({
                  type: prediction.type,
                  start: prediction.start,
                  end: prediction.end,
                  data: label_data || undefined,
                })
                  .toPairs()
                  .filter(([_k, v]) => v !== undefined)
                  .fromPairs()
                  .value()
                return resItem
              })
              .first()
              .value(),
          ])
          .fromPairs()
          .value(),
      ])
      .fromPairs()
      .value()
    return res
  }

  function labelMapDiffPairs(a?: IUserLabelsMap, b?: IUserLabelsMap) {
    const aggKeys = _.chain([_.toPairs(a), _.toPairs(b)])
      .flatten()
      .map(([type, v]) => {
        return _.chain(v)
          .keys()
          .map((ts) => `["${type}"]["${ts}"]`)
          .value()
      })
      .flatten()
      .uniq()
      .value()
    const diff: [string, ILabel<object> | undefined, ILabel<object> | undefined][] = _.chain(
      aggKeys
    )
      .map((key) => {
        const labelA = _.get(a, key) as ILabel<object> | undefined
        const labelB = _.get(b, key) as ILabel<object> | undefined
        if (_.isEqual(labelA, labelB)) return
        return [key, labelA, labelB] as [string, ILabel<object>, ILabel<object>]
      })
      .compact()
      .value()
    return diff
  }

  function labelMapDiffAsApiPatches(
    vionlabs_id: string,
    a?: IUserLabelsMap,
    b?: IUserLabelsMap
  ): IMutateUserLabelsPatchItem[] {
    const predictionFields: (keyof IEvent)[] = ['type', 'start', 'end']
    const diff = _.chain(labelMapDiffPairs(a, b))
      .map(([_key, labelA, labelB]) => {
        const _a = _.pick(labelA, predictionFields) as ILabel<object>
        const _b = _.pick(labelB, predictionFields) as ILabel<object>
        const _hasA = _a && _.keys(_a).length > 0
        const _hasB = _b && _.keys(_b).length > 0
        let res: IMutateUserLabelsPatchItem[] | undefined
        // have upstream label...
        if (_hasA) {
          // ...that shouldn't be
          if (!_hasB) {
            res = [
              {
                vionlabs_id,
                prediction: _a,
                label_data: null,
              },
            ]
          }
          // ...with changed timestamps
          else if (!_.isEqual(_a, _b)) {
            res = [
              {
                vionlabs_id,
                prediction: _a,
                label_data: null,
              },
              {
                vionlabs_id,
                prediction: _b,
                label_data: labelB?.data,
              },
            ]
          }
          // ...with changed label data
          else if (!_.isEqual(labelA?.data, labelB?.data)) {
            res = [
              {
                vionlabs_id,
                prediction: _b,
                label_data: labelB?.data,
              },
            ]
          }
        } else if (labelB) {
          res = [
            {
              vionlabs_id,
              prediction: _b,
              label_data: labelB.data,
            },
          ]
        }
        return res
      })
      .compact()
      .flatten()
      .value()
    return diff
  }

  // function exportLabels()
  // {
  //     const ydoc = yjsRef.current;
  //     const labels = ydoc.getMap('video-labels');;
  //     const res = (
  //         _.chain(labels.toJSON())
  //         .toPairs()
  //         .filter(([,v]) => _.keys(v).length > 0)
  //         .map(([k, v]) => {
  //             return {
  //                 vionlabs_id: k,
  //                 labels: (
  //                     _.chain(v)
  //                     .values()
  //                     .map(vv => _.values(vv))
  //                     .flatten()
  //                     .value()
  //                 ),
  //             }
  //         })
  //         .value()
  //     )
  //     const fName = `video-labels-${new Date().toISOString()}.jsonl`;
  //     exportAsFile(res, fName);
  //     // console.log('export', res);
  // }

  // function exportVerifiedPredictions()
  // {
  //     const ydoc = yjsRef.current;
  //     const labels = ydoc.getMap('video-labels');
  //     const res = (
  //         _.chain(labels.toJSON())
  //         .toPairs()
  //         .filter(([,v]) => _.keys(v).length > 0)
  //         .map(([k, v]) => {
  //             return {
  //                 vionlabs_id: k,
  //                 predictions: (
  //                     _.chain(v)
  //                     .values()
  //                     .map(vv => {
  //                         return (
  //                             _.chain(vv)
  //                             .values()
  //                             .filter(x => x.userScore > 3)
  //                             .value()
  //                         );
  //                     })
  //                     .flatten()
  //                     .value()
  //                 ),
  //             }
  //         })
  //         .filter(v => v.predictions.length > 0)
  //         .value()
  //     )
  //     const fName = `vionlabs-${new Date().toISOString()}-verified-predictions.jsonl`;
  //     exportAsFile(res, fName);
  //     // console.log('export', res);
  // }

  return (
    <LabelingContext.Provider
      value={{
        isLabelingAllowed: isLabelingAllowed(),
        userLabelResponses: userLabelResponses.current,
        fetchApiLabels,
        getCrdtLabelTrackCollection,
        getCrdtLabelTrack,
        haveLocalChanges,
        syncUpstreamLabels,
        // exportLabels,
        // exportVerifiedPredictions,
      }}
    >
      {/* {JSON.stringify(labelsDataRaw)} */}
      {children}
    </LabelingContext.Provider>
  )
}
